import { useState, useEffect } from 'react';
import { apiConfig, buildApiUrl } from '../config/api';

interface Activity {
  activity_type: 'note' | 'checkin' | 'workflow_activity';
  content: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  person_id: string | null;
  action: string;
  additional_info: string | null;
  event_name: string | null;
}

interface ActivityFeedData {
  activities: Activity[];
  summary: {
    totalActivities: number;
    dateRange: number;
    activityTypes: string[];
  };
}

export default function PeopleActivityFeed() {
  const [activityData, setActivityData] = useState<ActivityFeedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState(7);
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => {
    fetchActivityFeed();
  }, [selectedDays]);

  const fetchActivityFeed = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildApiUrl(`${apiConfig.endpoints.people.activityFeed}?days=${selectedDays}&limit=100`));
      
      if (!response.ok) {
        throw new Error(`Failed to fetch activity feed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setActivityData(data.data);
      } else {
        setError(data.error?.message || 'Failed to load activity feed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'note': return '📝';
      case 'checkin': return '✅';
      case 'workflow_activity': return '🔄';
      default: return '📋';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'note': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'checkin': return 'bg-green-100 text-green-800 border-green-200';
      case 'workflow_activity': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getActivityTypeLabel = (type: string) => {
    switch (type) {
      case 'note': return 'Note';
      case 'checkin': return 'Check-in';
      case 'workflow_activity': return 'Workflow';
      default: return 'Activity';
    }
  };

  const filteredActivities = activityData?.activities.filter(activity => 
    selectedType === 'all' || activity.activity_type === selectedType
  ) || [];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Activity Feed</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button
                onClick={fetchActivityFeed}
                className="mt-3 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!activityData) {
    return <div>No data available</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">People Activity Feed</h1>
        <p className="mt-2 text-gray-600">Real-time activity stream from Planning Center Online</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">📊</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Activities</dt>
                  <dd className="text-lg font-medium text-gray-900">{activityData.summary.totalActivities}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">✅</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Check-ins</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {activityData.activities.filter(a => a.activity_type === 'checkin').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">📝</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Notes</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {activityData.activities.filter(a => a.activity_type === 'note').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-medium">🔄</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Workflows</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {activityData.activities.filter(a => a.activity_type === 'workflow_activity').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
            <select
              value={selectedDays}
              onChange={(e) => setSelectedDays(parseInt(e.target.value))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Activity Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Activities</option>
              <option value="checkin">Check-ins</option>
              <option value="note">Notes</option>
              <option value="workflow_activity">Workflows</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activity Feed */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Recent Activity ({filteredActivities.length} items)
          </h3>
        </div>
        
        <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
          {filteredActivities.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <p className="text-gray-500">No activities found for the selected filters.</p>
            </div>
          ) : (
            filteredActivities.map((activity, index) => (
              <div key={`${activity.created_at}-${index}`} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getActivityColor(activity.activity_type)}`}>
                      <span className="mr-1">{getActivityIcon(activity.activity_type)}</span>
                      {getActivityTypeLabel(activity.activity_type)}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {activity.first_name && (
                          <span className="text-sm font-medium text-gray-900">
                            {activity.first_name} {activity.last_name}
                          </span>
                        )}
                        <span className="text-sm text-gray-500">•</span>
                        <span className="text-sm text-gray-500">{activity.action}</span>
                      </div>
                      <time className="text-sm text-gray-500">
                        {formatDate(activity.created_at)}
                      </time>
                    </div>
                    
                    <div className="mt-2">
                      <p className="text-sm text-gray-700 line-clamp-3">
                        {activity.content}
                      </p>
                    </div>
                    
                    {(activity.event_name || activity.additional_info) && (
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        {activity.event_name && (
                          <span className="inline-flex items-center">
                            📅 {activity.event_name}
                          </span>
                        )}
                        {activity.additional_info && activity.activity_type === 'checkin' && (
                          <span className="inline-flex items-center">
                            🔐 {activity.additional_info}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
